import React, { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n";
import { RevealText } from "./Reveal";

const IMG_BASE = "https://customer-assets-rejwkqb3.emergentagent.net/job_reformas-acero/artifacts";

const PAIRS = [
  {
    before: `${IMG_BASE}/i9mpg780_WhatsApp%20Image%202026-08-05%20at%2013.36.29.webp`,
    after: `${IMG_BASE}/2wzhp54z_WhatsApp%20Image%202026-08-05%20at%2013.36.30.webp`,
    es: "Techo en pladur · Instalación completa",
    en: "Drywall ceiling · Complete installation",
  },
  {
    before: `${IMG_BASE}/tsjaviw6_local%20antes.webp`,
    after: `${IMG_BASE}/0hyekplo_local%20despues.webp`,
    es: "Reforma de local · Suelo de mármol y trasdosado",
    en: "Commercial space renovation · Marble floor & lining",
  },
  {
    before: `${IMG_BASE}/d7crjt9f_d79534b5-601f-4ac2-9fa3-4f1e648fdcf8.jpg`,
    after: `${IMG_BASE}/b2548xq0_afa90d76-58c0-4f8d-b205-89a27a874f39.jpg`,
    es: "Rehabilitación de terraza · Impermeabilización",
    en: "Rooftop terrace rehabilitation · Waterproofing",
  },
];

export default function AntesDespues() {
  const { t, lang } = useLang();
  const a = t.antes;
  const g = t.galeria;
  const ref = useRef(null);
  const [pos, setPos] = useState(52);
  const [dragging, setDragging] = useState(false);
  const [active, setActive] = useState(0);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const pair = PAIRS[active];

  return (
    <section id="antes-despues" className="relative py-24 md:py-32 bg-surface grain">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-brand-orange" />
          <span className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-brand-orange">{a.overline}</span>
        </div>
        <RevealText
          as="h2"
          text={a.title}
          className="font-display font-semibold tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl block max-w-3xl"
        />
        <p className="mt-5 max-w-xl text-steel-300 md:text-lg">{a.sub}</p>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mt-12"
        >
          <div
            ref={ref}
            data-testid="antes-despues-slider"
            className="relative w-full aspect-[16/10] md:aspect-[16/9] overflow-hidden select-none cursor-ew-resize border border-border"
            onPointerDown={(e) => { setDragging(true); update(e.clientX); }}
            onPointerMove={(e) => dragging && update(e.clientX)}
            onPointerUp={() => setDragging(false)}
            onPointerLeave={() => setDragging(false)}
          >
            <img src={pair.after} alt={g.after} draggable={false} className="absolute inset-0 h-full w-full object-cover" />
            <span className="absolute top-4 right-4 z-10 bg-black/70 backdrop-blur text-white text-xs font-bold tracking-widest uppercase px-3 py-1.5">{g.after}</span>

            <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
              <div className="absolute inset-0" style={{ width: ref.current?.offsetWidth || "100%", height: "100%" }}>
                <img src={pair.before} alt={g.before} draggable={false} className="h-full w-full object-cover" />
              </div>
              <span className="absolute top-4 left-4 z-10 bg-brand-orange text-black text-xs font-bold tracking-widest uppercase px-3 py-1.5">{g.before}</span>
            </div>

            <div className="absolute top-0 bottom-0 z-20 -ml-px" style={{ left: `${pos}%` }}>
              <div className="h-full w-0.5 bg-brand-orange" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-brand-orange text-black grid place-items-center shadow-lg">
                <MoveHorizontal className="h-5 w-5" />
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-6 flex flex-wrap gap-3">
          {PAIRS.map((p, i) => (
            <button
              key={i}
              data-testid={`antesdespues-tab-${i}`}
              onClick={() => { setActive(i); setPos(52); }}
              className={`text-sm font-medium px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                active === i ? "bg-brand-orange text-black border-brand-orange" : "border-border text-steel-300 hover:border-white hover:text-white"
              }`}
            >
              {p[lang]}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
